import React from 'react';
import { SignificanceLevelIconInterface, StudyItemCardProps } from './types';
import style from './style.module.scss';
import cx from 'classnames';
import LessonIllustration from '../../assets/top_card_lesson.svg';
import StrategyIllustration from '../../assets/top_strategy_segment.svg';
import SegmentIllustration from '../../assets/top_non_strategy_segment.svg';
import PracticeCardIllustration1 from '../../assets/practice_card_1.svg';
import PracticeCardIllustration2 from '../../assets/practice_card_2.svg';
import PracticeCardIllustration3 from '../../assets/practice_card_3.svg';

import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import Icon, { SectionIcons, SectionIllustrations } from '@magoosh/gre/assets/icons';
import moment from 'moment';
import {
  HighSignificanceIcon,
  LowSignificanceIcon,
  ModerateSignificanceIcon
} from '@magoosh/gre/pages/mock_test_performance_summary/icons';
import { PremiumBadge } from '@magoosh/lib/info_badges';
import ContextualLink from '@magoosh/gre/app/contextual_link';

export const StudyItemCard: React.FC<StudyItemCardProps> = (props) => {
  const {
    type,
    section,
    headline,
    subheadline,
    significanceLevel,
    minutes,
    date,
    percentageCompleted,
    description,
    shortDescription,
    primaryCTA,
    secondaryCTA,
    variant,
    isLarger,
    locked,
    isStrategy,
    isCompleted
  } = props;

  const SignificanceLevelIcon: SignificanceLevelIconInterface = {
    High: <HighSignificanceIcon style={{ fill: style.gray, height: '17px', width: '22px' }} />,
    Moderate: <ModerateSignificanceIcon style={{ fill: style.gray, height: '17px', width: '22px' }} />,
    Low: <LowSignificanceIcon style={{ fill: style.gray, height: '17px', width: '22px' }} />
  };

  const iconType: { notCompleted: SectionIcons; completed: SectionIcons } = {
    notCompleted: {
      Math: 'Calc',
      Verbal: 'Notebook',
      Writing: 'Pen',
      GRE: 'Tutor'
    },
    completed: {
      Math: 'CalcCompleted',
      Verbal: 'NotebookCompleted',
      Writing: 'PenCompleted',
      GRE: 'TutorCompleted'
    }
  };

  const illustrationType: SectionIllustrations = {
    Math: <img key="2" src={PracticeCardIllustration2} alt="Practice Card Illustration" />,
    Verbal: <img key="1" src={PracticeCardIllustration1} alt="Practice Card Illustration" />,
    Writing: <img key="3" src={PracticeCardIllustration3} alt="Practice Card Illustration" />,
    GRE: <img key="3" src={PracticeCardIllustration3} alt="Practice Card Illustration" />
  };

  const renderPrimaryCTA = () => {
    if (!primaryCTA) return null;

    return (
      <ContextualLink
        className={`btn btn-${isCompleted ? 'default' : primaryCTA.variant}`}
        disabled={primaryCTA.isDisabled}
        {...primaryCTA}>
        {isCompleted ? 'Review' : primaryCTA.text}
      </ContextualLink>
    );
  };

  return (
    <div className={style.studyItemCard}>
      <Row>
        <Col xs={4}>
          <Icon
            iconType={iconType[isCompleted ? 'completed' : 'notCompleted'][section]}
            fill={variant === 'secondary' ? style.iconSecondary : style.brandInfo}
          />
        </Col>
        <Col xs={8}>
          <div className={cx(style.buttonHeader, 'u-margin-B-m')}>
            {!locked && secondaryCTA}
            {locked && <PremiumBadge className={'u-margin-H-xs'} />}

            {primaryCTA && <div className="u-margin-L-xs">{renderPrimaryCTA()}</div>}
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={isLarger ? 6 : 12}>
          <p className="u-margin-A-n" style={{ color: style.gray }}>
            {subheadline}
          </p>
          <h3 className={`u-margin-A-n ${isCompleted ? style.completedHeadline : ''}`}>
            {isLarger && description ? <strong>{headline}</strong> : headline}
          </h3>
          <p style={{ color: style.gray }}>
            {isStrategy && (
              <span className="u-margin-R-xs">
                <Icon iconType="Gem" /> Strategy
              </span>
            )}

            {significanceLevel && !isStrategy && (
              <span className="u-margin-R-xs">
                {SignificanceLevelIcon[significanceLevel]} {significanceLevel} Significance
              </span>
            )}

            {minutes && (
              <span className="u-margin-R-xs">
                <FontAwesomeIcon icon={'clock-o'} /> {minutes} min.
              </span>
            )}

            {date && (
              <span className="u-margin-R-xs">
                <FontAwesomeIcon icon={'calendar-check-o'} /> {moment(date).format('L')}{' '}
              </span>
            )}

            {percentageCompleted && (
              <span className="u-margin-R-xs">
                <FontAwesomeIcon icon={'check'} /> {percentageCompleted}% Complete
              </span>
            )}

            {shortDescription && !isLarger && <span className="u-margin-R-xs">{shortDescription}</span>}
          </p>
          {isLarger && description && <p>{description}</p>}
        </Col>
        {isLarger && (
          <Col md={6}>
            <div className={style.illustration}>
              {type === 'Lesson' && <img src={LessonIllustration} alt="Lesson Illustration" />}
              {type === 'Segment' && isStrategy && (
                <img src={StrategyIllustration} alt="Strategy Illustration" />
              )}
              {type === 'Segment' && !isStrategy && (
                <img src={SegmentIllustration} alt="Segment Illustration" />
              )}
              {variant === 'info' && illustrationType[section]}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default StudyItemCard;
